/**
 * PublicAuctionDetail — dark navy / yellow themed public view of an auction
 * (Iter30). Shows the tournament header + tabs (Teams / Players / Match).
 * Mobile-first: cards stack single-column on phones, 2-up on tablets, 3-up on
 * desktop. Colours match the reference screenshots (navy #0B1E3F, gold
 * #FCD34D, emerald tags, orange numbers).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resolveImageUrl } from "@/components/ImageInput";
import { Trophy, Gear, CaretLeft, CaretRight, SpeakerHigh, SpeakerSlash, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";

const CATEGORY_LABELS = ["ALL"]; // rendered + dynamic list below

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

export default function PublicAuctionDetail() {
  const { auctionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("teams");
  const [muted, setMuted] = useState(true);
  const [sortKey, setSortKey] = useState("");
  const [category, setCategory] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("All");
  const [teamIdx, setTeamIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get(`/public/auctions/${auctionId}`);
        if (alive) setData(r.data);
      } catch { if (alive) setData({}); }
    };
    load();
    const t = setInterval(load, 8000); // light polling — keeps the view fresh
    return () => { alive = false; clearInterval(t); };
  }, [auctionId]);

  const detailTeamData = null; // legacy — kept null since modal removed

  if (!data) return <FullPageMessage>Loading auction…</FullPageMessage>;
  if (!data.auction) return <FullPageMessage>Auction not found.</FullPageMessage>;

  const { auction, teams = [], players = [] } = data;
  const soldPlayers = players.filter((p) => p.status === "sold");
  const availPlayers = players.filter((p) => p.status === "available");
  const unsoldPlayers = players.filter((p) => p.status === "unsold");
  const categories = Array.from(new Set(players.map((p) => p.category).filter(Boolean)));

  // Team card enrichment
  const enrichedTeams = teams.map((t) => {
    const tp = players.filter((p) => p.sold_to === t.id);
    const spent = tp.reduce((s, p) => s + (p.sold_price || 0), 0);
    return { ...t, players_bought: tp.length, spent, players: tp };
  });

  return (
    <div className="min-h-screen text-white" style={{ background: "linear-gradient(180deg,#071731 0%,#0A2350 60%,#082042 100%)" }} data-testid="public-auction-detail">
      <BannerAndHeader auction={auction} user={user} auctionId={auctionId} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        {/* Top toolbar — mobile-first horizontal scroll */}
        <div className="sticky top-0 z-30 py-3 -mx-3 sm:-mx-6 px-3 sm:px-6 backdrop-blur bg-[#071731]/85 border-b border-white/5">
          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap overflow-x-auto">
            <SegmentBtn active={tab === "teams"} onClick={() => setTab("teams")} testId="pub-tab-teams">Teams</SegmentBtn>
            <SegmentBtn active={tab === "players"} onClick={() => setTab("players")} testId="pub-tab-players">Players</SegmentBtn>
            <SegmentBtn active={tab === "match"} onClick={() => setTab("match")} testId="pub-tab-match">Match</SegmentBtn>

            <div className="flex items-center gap-2 ml-auto">
              <Pill color="emerald" label="SOLD" value={soldPlayers.length} testId="pub-sold-count" />
              <Pill color="rose" label="UNSOLD" value={unsoldPlayers.length} testId="pub-unsold-count" />
              <Pill color="amber" label="AVL" value={availPlayers.length} testId="pub-avail-count" />
              <button type="button" onClick={() => setMuted((m) => !m)} className="text-yellow-300 hover:text-yellow-200 p-1.5" data-testid="pub-mute-toggle" aria-label="Toggle sound">
                {muted ? <SpeakerSlash weight="fill" size={20} /> : <SpeakerHigh weight="fill" size={20} />}
              </button>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="bg-[#0F2C5A] border border-white/10 rounded px-2 py-1.5 text-xs font-bold text-yellow-300 focus:border-yellow-400 outline-none"
                data-testid="pub-sort"
              >
                <option value="">Sort By ▼</option>
                <option value="name">Name</option>
                <option value="points-desc">Points ↓</option>
                <option value="points-asc">Points ↑</option>
                <option value="number">Number</option>
              </select>
            </div>
          </div>
        </div>

        {tab === "teams" && <TeamsSection teams={enrichedTeams} onOpen={(id) => navigate(`/auction/${auctionId}/team/${id}`)} auction={auction} />}
        {tab === "players" && (
          <PlayersSection
            auctionId={auctionId}
            players={applySort(applyCategory(applyStatus(players, statusFilter), category), sortKey)}
            teams={enrichedTeams}
            categories={categories}
            category={category}
            onCategory={setCategory}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
            totalCount={players.length}
          />
        )}
        {tab === "match" && (
          <TeamPlayerCarousel
            auctionId={auctionId}
            teams={enrichedTeams}
            teamIdx={teamIdx}
            setTeamIdx={setTeamIdx}
            categories={categories}
            category={category}
            onCategory={setCategory}
            sortKey={sortKey}
          />
        )}
      </div>
    </div>
  );
}

// ————————————————— helpers —————————————————
function applyStatus(list, s) {
  if (s === "AVL") return list.filter((p) => p.status === "available");
  if (s === "SOLD") return list.filter((p) => p.status === "sold");
  if (s === "UNSOLD") return list.filter((p) => p.status === "unsold");
  return list;
}
function applyCategory(list, c) {
  if (!c || c === "ALL") return list;
  if (c === "No category") return list.filter((p) => !p.category);
  return list.filter((p) => p.category === c);
}
function applySort(list, key) {
  const arr = [...list];
  if (key === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  else if (key === "points-desc") arr.sort((a, b) => (b.sold_price || b.base_price || 0) - (a.sold_price || a.base_price || 0));
  else if (key === "points-asc") arr.sort((a, b) => (a.sold_price || a.base_price || 0) - (b.sold_price || b.base_price || 0));
  else if (key === "number") arr.sort((a, b) => (a.player_number || 0) - (b.player_number || 0));
  return arr;
}

// ————————————————— presentational —————————————————
function FullPageMessage({ children }) {
  return (
    <div className="min-h-screen grid place-items-center text-yellow-300/80 label-mono text-lg" style={{ background: "#071731" }}>
      {children}
    </div>
  );
}

function SegmentBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={classNames(
        "text-xs sm:text-sm font-black uppercase tracking-widest px-4 sm:px-5 py-1.5 rounded-full whitespace-nowrap transition-all",
        active
          ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 shadow-lg shadow-yellow-500/20"
          : "text-white/80 hover:text-yellow-300",
      )}
    >{children}</button>
  );
}

function Pill({ color, label, value, testId }) {
  const map = {
    emerald: "bg-emerald-500/90 text-white border-emerald-300/40",
    rose: "bg-rose-500/90 text-white border-rose-300/40",
    amber: "bg-amber-500/90 text-slate-900 border-amber-300/40",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1.5 rounded-full border text-[10px] sm:text-xs font-black uppercase tracking-widest px-2.5 sm:px-3 py-1", map[color])} data-testid={testId}>
      {label} : <span className="tabular-nums text-white">{value}</span>
    </span>
  );
}

function BannerAndHeader({ auction, user, auctionId }) {
  const start = auction.start_date ? new Date(auction.start_date) : null;
  return (
    <div className="relative overflow-hidden">
      {auction.banner_url && (
        <img
          src={resolveImageUrl(auction.banner_url)}
          alt=""
          className="w-full max-h-52 sm:max-h-72 object-cover opacity-70"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#071731] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 pt-4 pb-6">
        <Link to="/" className="text-xs text-yellow-300/80 hover:text-yellow-200 inline-flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> All auctions
        </Link>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300/80 mb-1">
              {auction.status === "live" && <span className="live-dot mr-1 inline-block align-middle" />}
              {auction.status || "auction"}
              {start && <span className="text-white/60 ml-2">· {start.toLocaleDateString()}</span>}
            </div>
            <h1 className="font-display uppercase text-2xl sm:text-4xl leading-tight text-yellow-300 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{auction.name}</h1>
            {auction.description && <p className="text-white/70 mt-2 text-sm max-w-2xl">{auction.description}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {auction.status === "live" && (
              <Link to={`/overlay/${auctionId}/led`} target="_blank" className="rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest">Watch Live</Link>
            )}
            {user && auction.organizer && user.id === auction.organizer.id && (
              <Link to={`/auction/${auctionId}/manage`} className="rounded-full border border-yellow-300/60 text-yellow-200 px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1" data-testid="public-manage-button">
                <Gear size={12} /> Manage
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamsSection({ teams, onOpen }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-4" data-testid="pub-teams-grid">
      {teams.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No teams registered.</div>}
      {teams.map((t) => (
        <button
          type="button"
          key={t.id}
          onClick={() => onOpen(t.id)}
          className="text-left rounded-xl bg-[#0F2C5A] hover:bg-[#12356B] transition-colors border border-white/5 p-3 sm:p-4 flex items-center gap-3 sm:gap-4"
          data-testid={`pub-team-${t.id}`}
        >
          {t.logo_url ? (
            <img
              src={resolveImageUrl(t.logo_url)}
              alt=""
              className="w-14 h-14 sm:w-16 sm:h-16 rounded object-cover bg-white flex-shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded grid place-items-center flex-shrink-0" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
              <Trophy weight="fill" size={22} style={{ color: t.color }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-sm sm:text-base truncate">{t.name}</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="text-yellow-300 font-black text-2xl sm:text-3xl tabular-nums leading-none">{t.players_bought}</div>
              <div className="ml-auto text-right">
                <div className="text-yellow-300 font-black text-xl sm:text-2xl tabular-nums leading-none">{(t.budget || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1 text-[11px] sm:text-xs">
              <div className="text-white/70">Max <span className="text-white font-bold">{t.max_players || 15}</span></div>
              <div className="text-white/70">Res <span className="text-emerald-400 font-black tabular-nums">{(t.remaining_budget ?? 0).toLocaleString()}</span></div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function CategoryChips({ categories, category, onCategory, totalCount }) {
  const chips = ["ALL", ...categories];
  if (categories.length) chips.push("No category");
  return (
    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto py-2 sticky top-[52px] sm:top-[56px] bg-[#071731]/95 z-20 -mx-3 sm:-mx-6 px-3 sm:px-6" data-testid="pub-category-chips">
      {totalCount != null && <span className="text-yellow-300 font-black text-lg sm:text-xl tabular-nums pr-2">{totalCount}</span>}
      {chips.map((c) => (
        <button
          type="button"
          key={c}
          onClick={() => onCategory(c)}
          data-testid={`pub-cat-${c}`}
          className={classNames(
            "text-[11px] sm:text-xs font-black uppercase tracking-widest px-3 sm:px-4 py-1.5 rounded-md whitespace-nowrap transition-colors border",
            category === c
              ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 border-yellow-300"
              : "bg-transparent text-white/80 border-white/10 hover:border-yellow-400/50 hover:text-yellow-300",
          )}
        >{c}</button>
      ))}
    </div>
  );
}

function StatusFilterRow({ statusFilter, onStatusFilter }) {
  const items = ["All", "AVL", "SOLD", "UNSOLD"];
  return (
    <div className="flex items-center gap-2 flex-wrap py-1" data-testid="pub-status-filter">
      {items.map((k) => (
        <button
          key={k}
          onClick={() => onStatusFilter(k)}
          className={classNames(
            "text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-md border transition-colors",
            statusFilter === k
              ? "bg-white/10 text-yellow-300 border-yellow-400/60"
              : "text-white/70 border-white/10 hover:border-yellow-400/40",
          )}
          data-testid={`pub-status-${k}`}
        >{k}</button>
      ))}
    </div>
  );
}

function PlayerCard({ p, team, auctionId }) {
  const pts = p.sold_price != null ? p.sold_price : p.base_price;
  const isIcon = p.is_icon || p.category === "Icon";
  return (
    <div className={classNames("rounded-xl border overflow-hidden relative", isIcon ? "bg-[#1a2a4a] border-amber-400/70 ring-1 ring-amber-400/30" : "bg-[#0F2C5A] border-white/5")} data-testid={`pub-player-${p.id}`}>
      {p.player_number != null && (
        <span className="absolute top-2 left-2 z-10 bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900 font-black text-[10px] sm:text-xs px-2 py-0.5 rounded-md shadow" data-testid={`pub-player-num-${p.id}`}>{p.player_number}</span>
      )}
      {isIcon && (
        <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900 font-black text-[9px] sm:text-[10px] px-2 py-0.5 rounded-md tracking-widest shadow" data-testid={`pub-player-icon-${p.id}`}>★ ICON</span>
      )}
      {auctionId && (
        <a
          href={`/auction/${auctionId}/player/${p.id}/card`}
          target="_blank"
          rel="noreferrer"
          className="absolute top-2 right-2 z-10 rounded-md bg-black/70 hover:bg-yellow-400 hover:text-slate-900 text-yellow-300 p-1.5 transition-colors"
          title="Open downloadable player card"
          data-testid={`pub-player-download-${p.id}`}
          aria-label="Download player card"
        >
          <DownloadSimple weight="bold" size={14} />
        </a>
      )}
      <div className="flex items-stretch gap-3 p-3 pt-6">
        <div className="relative w-20 h-24 sm:w-24 sm:h-28 rounded-md overflow-hidden bg-black/30 flex-shrink-0">
          {p.photo_url ? (
            <img src={resolveImageUrl(p.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40 text-[10px]">NO PHOTO</div>
          )}
          {isIcon ? (
            <span className="absolute bottom-0 inset-x-0 text-center text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-900 bg-gradient-to-r from-amber-400 to-yellow-500 py-0.5 truncate px-1" title={p.name}>{p.name}</span>
          ) : (
            p.category && (
              <span className="absolute bottom-0 inset-x-0 text-center text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-black/50 py-0.5" data-testid={`pub-player-cat-${p.id}`}>{p.category}</span>
            )
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-yellow-300 font-black text-base sm:text-lg leading-tight truncate">{p.name}</div>
          {pts != null && (
            <div className="text-white text-xs sm:text-sm mt-1">
              <span className="text-white/60">Points : </span>
              <span className="font-black tabular-nums text-white">{pts.toLocaleString()}</span>
            </div>
          )}
          {p.role && <div className="text-white/80 text-xs sm:text-sm truncate">{p.role}</div>}
          {p.batting_style && <div className="text-white/70 text-xs sm:text-sm truncate">{p.batting_style}</div>}
          {p.bowling_style && <div className="text-white/70 text-xs sm:text-sm truncate">{p.bowling_style}</div>}
          {team && <div className="text-emerald-300 text-[11px] mt-1">→ {team.short_name || team.name}</div>}
        </div>
      </div>
    </div>
  );
}

function PlayersSection({ auctionId, players, teams, categories, category, onCategory, statusFilter, onStatusFilter, totalCount }) {
  const teamMap = Object.fromEntries(teams.map((t) => [t.id, t]));
  return (
    <div className="pb-8">
      <div className="flex items-center gap-3 flex-wrap py-2">
        <StatusFilterRow statusFilter={statusFilter} onStatusFilter={onStatusFilter} />
      </div>
      <CategoryChips categories={categories} category={category} onCategory={onCategory} totalCount={totalCount} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-2">
        {players.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No players in this view.</div>}
        {players.map((p) => (
          <PlayerCard key={p.id} p={p} team={p.sold_to ? teamMap[p.sold_to] : null} auctionId={auctionId} />
        ))}
      </div>
    </div>
  );
}

function TeamPlayerCarousel({ auctionId, teams, teamIdx, setTeamIdx, categories, category, onCategory, sortKey }) {
  const t = teams[teamIdx];
  if (!t) return <div className="text-center text-white/60 py-16">No teams yet.</div>;
  // Iter31: icon players always render first inside each team.
  const iconFirst = (arr) => [...arr].sort((a, b) => {
    const ai = (a.is_icon || a.category === "Icon") ? 0 : 1;
    const bi = (b.is_icon || b.category === "Icon") ? 0 : 1;
    return ai - bi;
  });
  const list = iconFirst(applyCategory(applySort(t.players || [], sortKey), category));
  const catCounts = categories.reduce((acc, c) => {
    acc[c] = (t.players || []).filter((p) => p.category === c).length;
    return acc;
  }, {});
  return (
    <div className="pb-8">
      <div className="flex items-center gap-3 py-3">
        <button onClick={() => setTeamIdx((i) => (i - 1 + teams.length) % teams.length)} className="p-2 rounded-full bg-white/5 hover:bg-yellow-400/20 text-yellow-300" data-testid="pub-team-prev"><CaretLeft weight="bold" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-xl sm:text-2xl truncate">{t.name}</div>
          <div className="text-yellow-300 text-lg sm:text-xl font-black tabular-nums">{t.players_bought}</div>
        </div>
        <button onClick={() => setTeamIdx((i) => (i + 1) % teams.length)} className="p-2 rounded-full bg-white/5 hover:bg-yellow-400/20 text-yellow-300" data-testid="pub-team-next"><CaretRight weight="bold" /></button>
      </div>
      <CategoryChips categories={categories} category={category} onCategory={onCategory} />
      <div className="flex flex-wrap gap-3 text-[11px] text-white/70 mt-2">
        {Object.entries(catCounts).map(([c, n]) => (
          <span key={c}><span className="text-white font-bold">{c}: {n}</span></span>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-3">
        {list.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No players yet.</div>}
        {list.map((p) => <PlayerCard key={p.id} p={p} team={t} auctionId={auctionId} />)}
      </div>
    </div>
  );
}
